import type {
  ClubSportEntry,
  SportConfig,
  SportOption,
  SportType,
} from '@models';

/**
 * The CATALOG slice of `SportConfig` — the only part that may be synced from
 * the repo defaults (`DEFAULT_SPORT_CONFIGS`) into a tenant document.
 *
 * Everything else in `SportConfig` is tenant IDENTITY and must never be
 * overwritten by a sync: `labels`, `positionLabels`, `gameUnitLabel`,
 * `setUnitLabel`, `setsToWinGame`, `gamesToWinMatch`, `hasPositions`,
 * `isPairSport`, `isRaceFormat`, `trackBalls`, `emailTemplates` (Brevo
 * template IDs) and `playerRankingResultTable` (scoring table). Overwriting
 * those would be destructive, so they are deliberately outside this type.
 */
export type SportCatalogFields = Pick<
  SportConfig,
  'availableScopes' | 'availableModalities' | 'competitionTypeLabels'
>;

/** What a sync would ADD to a tenant catalog. Never describes removals. */
export interface SportCatalogDiff {
  availableScopes: SportOption[];
  availableModalities: SportOption[];
  competitionTypeLabels: Record<string, string>;
}

type PartialCatalog = Partial<SportCatalogFields> | null | undefined;

function missingOptions(
  current: SportOption[] | undefined,
  repo: SportOption[] | undefined,
): SportOption[] {
  const known = new Set((current ?? []).map((o) => o.value));
  return (repo ?? []).filter((o) => !known.has(o.value));
}

function missingLabels(
  current: Record<string, string> | undefined,
  repo: Record<string, string> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, label] of Object.entries(repo ?? {})) {
    if (current && key in current) continue;
    out[key] = label;
  }
  return out;
}

/**
 * Entries present in the repo catalog and missing from the tenant one.
 *
 * Matching is by `value` (scopes/modalities) and by key (competition types),
 * NOT by label: a tenant that renamed an entry keeps its own label and the
 * entry is not reported as missing.
 */
export function diffSportCatalog(
  current: PartialCatalog,
  repo: Partial<SportCatalogFields>,
): SportCatalogDiff {
  return {
    availableScopes: missingOptions(
      current?.availableScopes,
      repo.availableScopes,
    ),
    availableModalities: missingOptions(
      current?.availableModalities,
      repo.availableModalities,
    ),
    competitionTypeLabels: missingLabels(
      current?.competitionTypeLabels,
      repo.competitionTypeLabels,
    ),
  };
}

/** Number of entries a sync would add. `0` ⇒ nothing to write. */
export function countSportCatalogDiff(diff: SportCatalogDiff): number {
  return (
    diff.availableScopes.length +
    diff.availableModalities.length +
    Object.keys(diff.competitionTypeLabels).length
  );
}

/** True when the tenant catalog already contains everything the repo has. */
export function isSportCatalogDiffEmpty(diff: SportCatalogDiff): boolean {
  return countSportCatalogDiff(diff) === 0;
}

/**
 * Strictly ADDITIVE merge of the repo catalog into a tenant sport config.
 *
 * - Existing entries win: same `value`/key ⇒ the tenant entry is kept verbatim
 *   (it may carry a customized label).
 * - Nothing is ever removed, not even entries the repo no longer knows about.
 * - Order: the tenant entries first, in their current order, additions appended.
 * - Only the three catalog fields are touched; every other property of
 *   `current` (labels, email templates, scoring table…) is passed through
 *   untouched.
 * - A catalog key the tenant does NOT have is only emitted when the merge
 *   produces something: an empty repo catalog (e.g. the `other` sport, whose
 *   `availableScopes`/`availableModalities` are `[]`) must not introduce an
 *   explicit empty array, because `SportConfigService` resolves its defaults
 *   with `??` and an explicit `[]` would suppress the fallback. A key the
 *   tenant already has is always kept, even when empty — the tenant's own data
 *   is never removed nor altered.
 */
export function mergeSportCatalog<T extends Partial<SportCatalogFields>>(
  current: T,
  repo: Partial<SportCatalogFields>,
): T {
  const diff = diffSportCatalog(current, repo);
  const merged: T = { ...current };
  const out = merged as Partial<SportCatalogFields>;

  const scopes = [...(current?.availableScopes ?? []), ...diff.availableScopes];
  if (keepKey(current?.availableScopes, scopes.length)) {
    out.availableScopes = scopes;
  }

  const modalities = [
    ...(current?.availableModalities ?? []),
    ...diff.availableModalities,
  ];
  if (keepKey(current?.availableModalities, modalities.length)) {
    out.availableModalities = modalities;
  }

  // NOT named `labels`: that is a tenant identity field and the source-level
  // guard in tests/admin/tenant-sport-catalog-sync/ asserts it never appears.
  const mergedTypeLabels = {
    ...(current?.competitionTypeLabels ?? {}),
    ...diff.competitionTypeLabels,
  };
  const typeLabelsCount = Object.keys(mergedTypeLabels).length;
  if (keepKey(current?.competitionTypeLabels, typeLabelsCount)) {
    out.competitionTypeLabels = mergedTypeLabels;
  }

  return merged;
}

/**
 * Whether the merged value of a catalog key must be written.
 *
 * Yes when the tenant already had the key (its own data always wins, even an
 * empty catalog it deliberately stores) or when the merge actually produced
 * entries. No when it would create a brand-new empty key — see the note on
 * `mergeSportCatalog`.
 */
function keepKey(currentValue: unknown, mergedSize: number): boolean {
  return currentValue !== undefined || mergedSize > 0;
}

// ─── Read-through sync plan ─────────────────────────────────────────────────

/**
 * The ONLY slice of a tenant document the sync looks at.
 *
 * Deliberately narrow: the plan and the payload below are computed from the
 * STORED document, never from the state of an edit form. Anything the user is
 * typing on screen is, by construction, not an input here — so it can neither
 * ride along into the write nor be lost by it.
 */
export interface SportCatalogTenant {
  sport: SportType;
  sportConfig?: SportConfig;
  type?: 'federation' | 'club';
  clubSports?: ClubSportEntry[];
}

/**
 * Repo defaults per sport (`DEFAULT_SPORT_CONFIGS`), injected as a parameter
 * to keep this file free of runtime imports.
 */
export type SportCatalogRepo = Partial<
  Record<SportType, Partial<SportCatalogFields>>
>;

/** One catalog inside the tenant document and what a sync would add to it. */
export interface SportCatalogPlanTarget {
  /** `null` ⇒ the root config; otherwise the index inside `clubSports`. */
  index: number | null;
  sport: SportType;
  diff: SportCatalogDiff;
  /** Entries this target would gain. `0` ⇒ it is already up to date. */
  additions: number;
  /** The tenant config with the additions merged in, ready to be written. */
  merged: SportConfig;
}

/** Every catalog of a tenant document, diffed against the repo defaults. */
export interface SportCatalogSyncPlan {
  targets: SportCatalogPlanTarget[];
  /** Entries the whole sync would add. `0` ⇒ nothing to write. */
  additions: number;
}

/**
 * What the sync would write. Exactly two optional keys, both derived from the
 * merge: nothing else may travel in the update — not `federationIds`, not
 * `branding`, not `name`, nothing the sync did not compute.
 */
export interface SportCatalogSyncPayload {
  sportConfig?: SportConfig;
  clubSports?: ClubSportEntry[];
}

function planTarget(
  index: number | null,
  sport: SportType,
  config: SportConfig | undefined,
  repo: SportCatalogRepo,
): SportCatalogPlanTarget {
  // A tenant may carry a sport the repo no longer ships: nothing to add then.
  const defaults: Partial<SportCatalogFields> = repo[sport] ?? {};
  const current = config ?? ({} as SportConfig);
  const diff = diffSportCatalog(current, defaults);
  return {
    index,
    sport,
    diff,
    additions: countSportCatalogDiff(diff),
    merged: mergeSportCatalog(current, defaults),
  };
}

/**
 * Diffs every catalog of the tenant document against the repo defaults of ITS
 * OWN sport: the root config plus, for clubs, one target per `clubSports`
 * entry. A multi-sport club gets one diff per discipline.
 *
 * The plan is the single source of truth for BOTH the preview shown to the
 * user and the payload that gets written, so what is announced is what is
 * persisted.
 */
export function planSportCatalogSync(
  tenant: SportCatalogTenant,
  repo: SportCatalogRepo,
): SportCatalogSyncPlan {
  const targets: SportCatalogPlanTarget[] = [
    planTarget(null, tenant.sport, tenant.sportConfig, repo),
  ];

  if (tenant.type === 'club') {
    (tenant.clubSports ?? []).forEach((entry, index) =>
      targets.push(planTarget(index, entry.sport, entry.sportConfig, repo)),
    );
  }

  return {
    targets,
    additions: targets.reduce((total, t) => total + t.additions, 0),
  };
}

/**
 * The Firestore update of a sync, built from the SAME document the plan was
 * computed on.
 *
 * - `sportConfig` travels only when the root target actually adds something.
 * - `clubSports` is rebuilt from the entries of the STORED document (same
 *   order, same count, same federation link); only the configs of the targets
 *   with additions are replaced by their merge. An entry added, removed or
 *   re-assigned to another sport in an unsaved form cannot appear here.
 * - Nothing else is ever included: an empty object means "nothing to write".
 */
export function sportCatalogSyncPayload(
  tenant: SportCatalogTenant,
  plan: SportCatalogSyncPlan,
): SportCatalogSyncPayload {
  const payload: SportCatalogSyncPayload = {};

  const root = plan.targets.find((t) => t.index === null);
  if (root && root.additions > 0) payload.sportConfig = root.merged;

  const stored = tenant.clubSports;
  const clubTargets = plan.targets.filter(
    (t) => t.index !== null && t.additions > 0,
  );
  if (stored && clubTargets.length > 0) {
    payload.clubSports = stored.map((entry, index) => {
      const target = clubTargets.find((t) => t.index === index);
      return target ? { ...entry, sportConfig: target.merged } : entry;
    });
  }

  return payload;
}
